import React from "react";
import { Box, Switch, Typography, useTheme } from "@mui/material";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { setReduxSignRequest } from "redux/appSlice";

/**
 * Step 5 — "Preferences" (Config) screen of the mobile sign-request flow.
 * Reuses the same redux fields as the desktop RequestConfiguration:
 *   - needReminders + reminders.{daysBeforeFirstReminder,daysBetweenReminders}
 *   - expirationDays
 *   - signRequestConfig.{allowDocumentDownload, sendNotificationOnCompletion,
 *       attachDocumentToMailOnCompletion, allowSignatureDelegation}
 */
const MobileConfig: React.FC = () => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const dispatch = useAppDispatch();

  const { signRequest } = useAppSelector((state: RootState) => state.app);
  const config = signRequest.signRequestConfig;

  const sanitizeDays = (value: string) => value.replace(/[^0-9]/g, "");

  const setReminderDays = (name: string, value: string) => {
    dispatch(
      setReduxSignRequest({
        reminders: { ...signRequest.reminders, [name]: sanitizeDays(value) },
      }),
    );
  };

  const setConfigFlag = (name: string, checked: boolean) => {
    dispatch(
      setReduxSignRequest({
        signRequestConfig: {
          ...signRequest.signRequestConfig,
          [name]: checked,
        },
      }),
    );
  };

  const expiryLabel =
    signRequest.expirationDays && Number(signRequest.expirationDays) > 0
      ? moment()
          .add(Number(signRequest.expirationDays), "days")
          .format("MMMM D, YYYY")
      : null;

  const notifyOn = !!config?.sendNotificationOnCompletion;

  // ---- Reusable pieces ---------------------------------------------------
  const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2,
        borderRadius: "16px",
        border: `1px solid ${m.grey[50]}`,
        bgcolor: m.surface.white,
      }}
    >
      {children}
    </Box>
  );

  // Full-bleed divider: breaks out of the card's 16px padding to span the card
  // edge-to-edge (matches the Figma `w-359` dividers inside the 361px cards).
  const Divider = () => (
    <Box sx={{ height: 0, borderTop: `1px solid ${m.grey[50]}`, mx: -2 }} />
  );

  const Title = ({ children }: { children: React.ReactNode }) => (
    <Typography
      sx={{
        fontSize: "0.875rem",
        fontWeight: 700,
        lineHeight: "21px",
        color: m.base[900],
      }}
    >
      {children}
    </Typography>
  );

  const Subtitle = ({ children }: { children: React.ReactNode }) => (
    <Typography
      sx={{ fontSize: "0.75rem", fontWeight: 500, color: m.grey[400] }}
    >
      {children}
    </Typography>
  );

  const DayInput = ({
    value,
    onChange,
    ariaLabel,
  }: {
    value: string | number | undefined;
    onChange: (v: string) => void;
    ariaLabel: string;
  }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      <Box
        component="input"
        inputMode="numeric"
        aria-label={ariaLabel}
        value={value ?? ""}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        sx={{
          width: 69,
          border: `1px solid ${m.grey[50]}`,
          borderRadius: "12px",
          px: 1.5,
          py: 1,
          textAlign: "center",
          fontSize: "0.875rem",
          fontWeight: 700,
          color: m.base[900],
          outline: "none",
          bgcolor: m.surface.white,
          "&:focus": { borderColor: m.ePurple[500] },
        }}
      />
      <Typography
        sx={{ fontSize: "0.875rem", fontWeight: 500, color: m.base[900] }}
      >
        Days
      </Typography>
    </Box>
  );

  const PermissionRow = ({
    title,
    subtitle,
    checked,
    onToggle,
    disabled,
  }: {
    title: string;
    subtitle: string;
    checked: boolean;
    onToggle: (checked: boolean) => void;
    disabled?: boolean;
  }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.5,
        width: "100%",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          minWidth: 0,
        }}
      >
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, value) => onToggle(value)}
        inputProps={{ "aria-label": title }}
      />
    </Box>
  );

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, px: 2, pt: 2 }}
    >
      {/* Automatic reminders */}
      <Card>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Title>Automatic reminders</Title>
            <Subtitle>Enable automatic reminder emails to signers</Subtitle>
          </Box>
          <Switch
            checked={!!signRequest.needReminders}
            onChange={(_, checked) =>
              dispatch(setReduxSignRequest({ needReminders: checked }))
            }
            inputProps={{ "aria-label": "Automatic reminders" }}
          />
        </Box>

        <Divider />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: 1,
          }}
        >
          <Title>First reminder after</Title>
          <DayInput
            value={signRequest.reminders?.daysBeforeFirstReminder}
            onChange={(v) => setReminderDays("daysBeforeFirstReminder", v)}
            ariaLabel="First reminder after, number of days"
          />
        </Box>

        <Divider />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: 1,
          }}
        >
          <Title>Follow-up reminders every</Title>
          <DayInput
            value={signRequest.reminders?.daysBetweenReminders}
            onChange={(v) => setReminderDays("daysBetweenReminders", v)}
            ariaLabel="Follow-up reminders every, number of days"
          />
        </Box>
      </Card>

      {/* Request Expiration */}
      <Card>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Title>Request Expiration</Title>
          <Subtitle>Request will expire after</Subtitle>
        </Box>

        <Divider />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            gap: 1,
          }}
        >
          <Title>Request will expire after</Title>
          <DayInput
            value={signRequest.expirationDays}
            onChange={(v) =>
              dispatch(setReduxSignRequest({ expirationDays: sanitizeDays(v) }))
            }
            ariaLabel="Request will expire after, number of days"
          />
        </Box>

        {expiryLabel && (
          <Box
            sx={{
              width: "100%",
              borderRadius: "8px",
              bgcolor: m.yellow[100],
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                lineHeight: 1.5,
                color: m.yellow[800],
                p: 1,
              }}
            >
              The sign request will be set to expire on {expiryLabel}
            </Typography>
          </Box>
        )}
      </Card>

      {/* Signer permissions */}
      <Card>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Title>Signer permissions</Title>
          <Subtitle>Set request configurations for the signers</Subtitle>
        </Box>

        <Divider />
        <PermissionRow
          title="Download documents"
          subtitle="Allow signers to download the document"
          checked={!!config?.allowDocumentDownload}
          onToggle={(v) => setConfigFlag("allowDocumentDownload", v)}
        />

        <Divider />
        <PermissionRow
          title="Notify"
          subtitle="Notify signer via mail after completion of the request"
          checked={notifyOn}
          onToggle={(v) => setConfigFlag("sendNotificationOnCompletion", v)}
        />

        <Divider />
        <PermissionRow
          title="Attachments"
          subtitle="Attach documents with mail of the request completion"
          checked={!!config?.attachDocumentToMailOnCompletion}
          onToggle={(v) => setConfigFlag("attachDocumentToMailOnCompletion", v)}
          disabled={!notifyOn}
        />

        <Divider />
        <PermissionRow
          title="Delegation"
          subtitle="Allow signer to delegate and let others sign on their behalf"
          checked={!!config?.allowSignatureDelegation}
          onToggle={(v) => setConfigFlag("allowSignatureDelegation", v)}
        />
      </Card>
    </Box>
  );
};

export default MobileConfig;
